<template>
  <v-dialog
    v-model="localDialog"
    width="50%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <base-card
      :title="isEntryMode ? 'Add Port' : 'Update Port'"
      :v-card-text-class="'pa-4'"
    >
<v-form @submit.prevent="submit" ref="LcPortSetup">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="country_id.value.value"
                :error-messages="country_id.errorMessage.value"
                clearable
                :items="getCountryList"
                :item-title="(item) => `${item.name}`"
                autocomplete="off"
                item-value="id"
                :menu-props="{ width: 300, contentClass: 'my-custom-dropdown'}"
                @update:modelValue="getCountryList"
              >
                <template #label>
                  Country <span class="text-red-accent-4">*</span>
                </template>
                <template v-slot:selection="{ props, item }">
                  <div class="d-flex align-center noto-color-emoji-regular"
                    v-bind="props"
                    color="primary"
                    variant="tonal"
                  >
                    <span class="mr-1">{{ item.raw.emoji }}</span>
                    {{ item.raw.name }}
                  </div>
                </template>
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props" v-slot="" :title="null">
                    <div class="d-flex align-center noto-color-emoji-regular" style="width: 100%;">
                      <span class="mr-2 text-lg">{{ item.raw.emoji }}</span>
                      <div class="flex-grow-1">
                        <span>{{ item.raw.name }}</span>
                        <small v-if="item.raw.native" class="text-grey">
                          ({{ item.raw.native }})
                        </small>
                      </div>
                      <div class="text-grey text-caption">
                        +{{ item.raw.phonecode }}
                      </div>
                    </div>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <v-text-field
                density="compact" 
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="name.value.value"
                :error-messages="name.errorMessage.value"
                clearable
              >
                <template #label>
                  Port Name <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-textarea
                label="Remarks (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
                auto-grow
                rows="2"
              ></v-textarea>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                hide-details
                color="primary"
                true-value="1"
                false-value="0"
              ></v-checkbox>
            </v-col>

            <input v-model="lc_port_id.value.value" type="hidden" />
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </base-card>
  </v-dialog>
</template>

<script>
import baseCard from '@/components/utils/baseCard.vue';
import { ref } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  components: { baseCard },
  name: "LcPortSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        country_id: "",
        name: "",
        remarks: "",
        status: "1",
        lc_port_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      isEntryMode: true,
    };
  },
  computed: {
    getCountryList() {
      return this.$store.getters.getInvCountryList || [];
    },
  },
  created() {
    // Load country list
    // if (this.$store.getters.getInvCountryList.length == 0) {
      this.$store.dispatch('getInvCountryList');
    // }
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.country_id.setValue(newData.country_id || "", false);
        this.name.setValue(newData.name || "", false);
        this.remarks.setValue(newData.remarks || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.lc_port_id.setValue(newData.id || "", false);
      },
    },
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        country_id: yup.string().required().label("Country"),
        name: yup.string().min(1).max(255).required().trim().label("Port Name"),
        remarks: yup.string().max(255).nullable().trim().label("Remarks"),
      }),
    });

    const country_id = useField("country_id");
    const name = useField("name");
    const remarks = useField("remarks");
    const status = useField("status");
    const lc_port_id = useField("lc_port_id");
    const store = useStore();

    const submit = handleSubmit((values) => {
      store
        .dispatch("saveUpdateLcPort", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          store.dispatch("getLcPortList");
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to save");
        });
    });

    return {
      submit,
      country_id,
      name,
      remarks,
      status,
      lc_port_id,
      resetForm,
    };
  },
};
</script>

<style scoped>
.bg-brand {
  background-color: #1976d2;
  color: white;
}
</style>

